import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { getApi, type CoachBrainApi } from "../api";
import { ApiError } from "../api/client";

export function useApi() {
  const [api, setApi] = useState<CoachBrainApi | null>(null);
  useEffect(() => { let on = true; void getApi().then((a) => { if (on) setApi(a); }); return () => { on = false; }; }, []);
  return api;
}

export function useLoad<T>(fn: (api: CoachBrainApi) => Promise<T>, deps: unknown[] = []) {
  const api = useApi(); const navigate = useNavigate();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!api) return;
    let on = true; setError(null);
    fn(api).then((d) => { if (on) setData(d); }).catch((e: unknown) => {
      if (!on) return;
      if (e instanceof ApiError && e.status === 401) { void navigate({ to: "/login", search: { next: window.location.pathname + window.location.search } }); return; }
      setError(e instanceof Error ? e.message : String(e));
    });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, tick, ...deps]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { api, data, error, reload, setData };
}

export function useUnits(): "lb" | "kg" {
  const [u, setU] = useState<"lb" | "kg">(() => { try { return (localStorage.getItem("cb-units") as "lb" | "kg") || "lb"; } catch { return "lb"; } });
  useEffect(() => { const h = () => { try { setU((localStorage.getItem("cb-units") as "lb" | "kg") || "lb"); } catch { /* ignore */ } }; window.addEventListener("cb-units", h); return () => window.removeEventListener("cb-units", h); }, []);
  return u;
}
export function setUnits(u: "lb" | "kg") { try { localStorage.setItem("cb-units", u); } catch { /* ignore */ } window.dispatchEvent(new Event("cb-units")); }

export function useOnline() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => { const on = () => setOnline(true), off = () => setOnline(false); window.addEventListener("online", on); window.addEventListener("offline", off); return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); }; }, []);
  return online;
}
